import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { Checkbox, Input } from "semantic-ui-react";
import useAxios from "axios-hooks";
import { Button, Text } from "components/shared";
import { getLiveParams } from "selectors/liveParams";

// INFO tab (2026-10-05 request, laid out after a reference SCADA's
// "Information / Additional information / Controller notifications" page).
//
// Device fields come from windTurbine.deviceInfo - PF2 V0.9.45+ sends them
// in its retained MQTT status message (serial number, Vestas controller
// serial as "Controller ID", serial line settings). Owner fields come from
// windTurbine.owner and are edited right here. Anything not provided shows
// N/A rather than an invented value.

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(22rem, 1fr));
  gap: 1rem;
  padding: 1rem 0;
  align-items: start;
`;

const Card = styled.div`
  background-color: ${(props) => props.theme.controlsBackgroundColor};
  border-radius: 12px;
  padding: 1rem 1.25rem 1.25rem;
`;

const CardTitle = styled((props) => <Text {...props} />)`
  display: block;
  color: ${(props) => props.theme.textDark};
  font-weight: 700;
  padding-bottom: 0.5rem;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid ${(props) => props.theme.textMediumLight};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.3rem 0;
  min-height: 2rem;
`;

const Label = styled.span`
  color: ${(props) => props.theme.textDark};
  font-weight: 700;
  font-size: 0.9rem;
  white-space: nowrap;
`;

const Value = styled.span`
  color: ${(props) => props.theme.textMediumDark};
  font-size: 0.9rem;
  text-align: right;
  word-break: break-word;
`;

const LinkValue = styled.a`
  color: ${(props) => props.theme.accentColor};
  font-size: 0.9rem;
  text-align: right;
  word-break: break-word;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const ErrorText = styled.div`
  color: ${(props) => props.theme.errorColor || "#db2828"};
  font-size: 0.85rem;
  margin-top: 0.5rem;
  text-align: right;
`;

const Note = styled.p`
  color: ${(props) => props.theme.textMedium};
  font-style: italic;
  font-size: 0.85rem;
  margin-top: 0.75rem;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: 0.75rem 1rem;

  .ui.checkbox label {
    color: ${(props) => props.theme.textDark} !important;
  }
`;

const NA = "N/A";
const show = (value, suffix = "") =>
  value === undefined || value === null || value === "" ? NA : `${value}${suffix}`;

const PARITY_TEXT = { N: "None", E: "Even", O: "Odd" };

const OWNER_FIELDS = [
  { key: "name", label: "Full name" },
  { key: "phone", label: "Phone number", href: (v) => `tel:${v.replace(/\s/g, "")}` },
  { key: "address", label: "Address" },
  { key: "email", label: "Email", href: (v) => `mailto:${v}` },
  { key: "serviceProvider", label: "Service provider" },
];

const NOTIFICATIONS = [
  { key: "connections", label: "Receive mails on connections/disconnections" },
  { key: "errors", label: "Receive mails on errors" },
  { key: "warnings", label: "Receive mails on manual warnings" },
];

const InfoRow = ({ label, value }) => (
  <Row>
    <Label>{label}:</Label>
    <Value>{value}</Value>
  </Row>
);

// Which rows apply depends on how the turbine type talks to its
// controller (2026-10-05): Vestas (PF2) is a serial RCS link, so its
// serial-line settings and controller serial matter; the Modbus TCP/IP
// types (Sorel, Rago, Nordtank - via modbus-mqtt-bridge) have none of
// that, so those rows are left out rather than shown as N/A.
const SERIAL_LINK_SCHEMAS = ["vestas"];

const Information = ({ windTurbine, liveProps }) => {
  const device = windTurbine.deviceInfo || {};
  const parity = device.controllerParity;
  const schemaId = windTurbine.type && windTurbine.type.schemaId;
  const serialLink = SERIAL_LINK_SCHEMAS.includes(schemaId);
  return (
    <Card>
      <CardTitle size={18}>Information</CardTitle>
      <InfoRow label="Rated turbine power" value={show(windTurbine.maxPower, " kW")} />
      <InfoRow label="Schema type" value={show(windTurbine.type && windTurbine.type.name)} />
      {/* Only when the schema says so - legacy xlsx-configured types have no schemaId. */}
      {schemaId && (
        <InfoRow label="Communication" value={serialLink ? "Serial (Vestas RCS)" : "Modbus TCP/IP"} />
      )}
      <InfoRow label="Software version" value={show(device.firmwareVersion)} />
      {/* PF2 reports its own serial (V0.9.45+). A modbus-mqtt-bridge is
          provisioned with the turbine identifier as its serial number
          (Orbital-server provisioning claim matches WindTurbine.identifier),
          and doesn't report one itself - shown from the identifier, no
          bridge firmware change needed (2026-10-06, turbine 002). */}
      <InfoRow
        label="Serial number"
        value={show(device.serialNumber || (!serialLink && schemaId ? windTurbine.identifier : null))}
      />
      <InfoRow label="Controller state" value={show(liveProps && liveProps.state)} />
      {serialLink && (
        <>
          <InfoRow label="Controller ID" value={show(device.controllerId)} />
          <InfoRow label="Service serial baudrate" value={show(device.serviceBaud)} />
          <InfoRow label="Controller serial baudrate" value={show(device.controllerBaud)} />
          <InfoRow
            label="Controller serial parity"
            value={parity ? PARITY_TEXT[parity] || parity : NA}
          />
        </>
      )}
    </Card>
  );
};

const OwnerInformation = ({ windTurbine, owner, setOwner }) => {
  const [draft, setDraft] = useState(null); // null = not editing
  const [{ loading, error }, save] = useAxios(
    { url: `/windTurbines/${windTurbine.id}/owner`, method: "PUT" },
    { manual: true }
  );

  const onSave = async () => {
    try {
      const response = await save({ data: draft });
      setOwner(response.data || draft);
      setDraft(null);
    } catch (e) {
      // error shown below via useAxios' error
    }
  };

  return (
    <Card>
      <CardTitle size={18}>Owner information</CardTitle>
      {OWNER_FIELDS.map(({ key, label, href }) => (
        <Row key={key}>
          <Label>{label}:</Label>
          {draft ? (
            <Input
              size="small"
              value={draft[key] || ""}
              onChange={(e, { value }) => setDraft({ ...draft, [key]: value })}
              style={{ width: "60%" }}
            />
          ) : owner[key] && href ? (
            <LinkValue href={href(owner[key])}>{owner[key]}</LinkValue>
          ) : (
            <Value>{show(owner[key])}</Value>
          )}
        </Row>
      ))}
      <Actions>
        {draft ? (
          <>
            <Button secondary onClick={() => setDraft(null)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={onSave} loading={loading} disabled={loading}>
              Save
            </Button>
          </>
        ) : (
          <Button onClick={() => setDraft({ ...owner })}>Edit</Button>
        )}
      </Actions>
      {error && <ErrorText>Could not save the owner information.</ErrorText>}
    </Card>
  );
};

// Per-turbine opt-ins, saved on each click (no Save button, like the
// reference page). The e-mails go to the Owner information e-mail - sent by
// Orbital-server's services/notifications.js: connections (no data for 10
// min / back again) and errors; "manual warnings" is stored but has no
// trigger yet.
const ControllerNotifications = ({ windTurbine, ownerEmail }) => {
  const [prefs, setPrefs] = useState(null);
  const [status, setStatus] = useState(null); // null | "saving" | "saved" | "error"
  const [{ data, error: loadError }] = useAxios({ url: `/windTurbines/${windTurbine.id}/notifications` });
  const [, save] = useAxios(
    { url: `/windTurbines/${windTurbine.id}/notifications`, method: "PUT" },
    { manual: true }
  );

  useEffect(() => {
    if (data) setPrefs(data);
  }, [data]);

  const onToggle = async (key, checked) => {
    const previous = prefs;
    const next = { ...prefs, [key]: checked };
    setPrefs(next);
    setStatus("saving");
    try {
      await save({ data: next });
      setStatus("saved");
    } catch (e) {
      setPrefs(previous);
      setStatus("error");
    }
  };

  return (
    <Card>
      <CardTitle size={18}>Controller notifications</CardTitle>
      {loadError && <ErrorText>Could not load your notification settings.</ErrorText>}
      <CheckboxGrid>
        {NOTIFICATIONS.map(({ key, label }) => (
          <Checkbox
            key={key}
            label={label}
            checked={!!(prefs && prefs[key])}
            disabled={!prefs || status === "saving"}
            onChange={(e, { checked }) => onToggle(key, checked)}
          />
        ))}
      </CheckboxGrid>
      {status === "saved" && <Note>Saved.</Note>}
      {status === "error" && <ErrorText>Could not save - please try again.</ErrorText>}
      {!ownerEmail && (
        <ErrorText>No e-mail set in Owner information - nothing will be sent.</ErrorText>
      )}
      <Note>
        Note: e-mails go to the Owner information e-mail
        {ownerEmail ? ` (${ownerEmail})` : ""}: on disconnection (no data for 10 minutes) and
        reconnection, and when the turbine reports an error.
      </Note>
    </Card>
  );
};

// Remote firmware update (admins, PF2/Vestas only): uploads the .bin to
// Orbital-server, which has the board download it itself (pull OTA,
// PF2 V0.9.46+ - the Webpages tunnel can't carry a firmware upload).
// Progress comes from the board's status message (deviceInfo.ota), polled
// every 2s while an update is running.
const OTA_STATE_TEXT = {
  downloading: "Downloading",
  verifying: "Verifying",
  rebooting: "Rebooting into the new firmware",
  failed: "Failed",
};

const FirmwareUpdate = ({ windTurbine, onRefresh }) => {
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState(null);
  const [polling, setPolling] = useState(false);
  const [{ loading }, upload] = useAxios(
    { url: `/windTurbines/${windTurbine.id}/ota`, method: "POST" },
    { manual: true }
  );
  const device = windTurbine.deviceInfo || {};
  const ota = device.ota;
  const versionAtStart = React.useRef(null);
  const fileInput = React.useRef(null);

  useEffect(() => {
    if (!polling) return undefined;
    const id = setInterval(onRefresh, 2000);
    // Stop watching after 5 min - e.g. re-flashing the version it already
    // runs never shows a version change to detect.
    const stop = setTimeout(() => {
      setPolling(false);
      setMessage("No confirmation after 5 minutes - check Software version above (reload the tab).");
    }, 5 * 60 * 1000);
    return () => {
      clearInterval(id);
      clearTimeout(stop);
    };
  }, [polling, onRefresh]);

  useEffect(() => {
    if (!polling) return;
    if (ota && ota.state === "failed") {
      setPolling(false);
      setMessage(`Update failed: ${ota.error || "unknown error"}. The board keeps its current firmware.`);
    } else if (
      versionAtStart.current &&
      device.firmwareVersion &&
      device.firmwareVersion !== versionAtStart.current &&
      !ota
    ) {
      setPolling(false);
      setMessage(`Done - the board now runs ${device.firmwareVersion}.`);
    }
  }, [polling, ota, device.firmwareVersion]);

  const onStart = async () => {
    if (!file) return;
    setMessage(null);
    versionAtStart.current = device.firmwareVersion || null;
    try {
      await upload({ data: file, headers: { "Content-Type": "application/octet-stream" } });
      setMessage("Update started - the board is downloading the firmware.");
      setPolling(true);
    } catch (e) {
      const reason = e && e.response && e.response.data && (e.response.data.error || e.response.data.message);
      setMessage(`Could not start the update${reason ? `: ${reason}` : ""}.`);
    }
  };

  return (
    <Card>
      <CardTitle size={18}>Firmware update</CardTitle>
      <InfoRow label="Current version" value={show(device.firmwareVersion)} />
      {ota && (
        <InfoRow
          label="Update status"
          value={`${OTA_STATE_TEXT[ota.state] || ota.state}${
            ota.state === "downloading" ? ` ${ota.progress || 0}%` : ""
          }`}
        />
      )}
      {/* The native file input rendered dark-on-dark (invisible) in the dark
          theme - hidden, opened from a regular button instead. */}
      <input
        ref={fileInput}
        type="file"
        accept=".bin"
        style={{ display: "none" }}
        onChange={(e) => setFile(e.target.files && e.target.files[0])}
      />
      <Row>
        <Button secondary disabled={loading || polling} onClick={() => fileInput.current && fileInput.current.click()}>
          Choose file…
        </Button>
        <Value>{file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : "No file chosen"}</Value>
      </Row>
      <Actions>
        <Button onClick={onStart} loading={loading} disabled={!file || loading || polling}>
          Upload and update
        </Button>
      </Actions>
      {message && <Note>{message}</Note>}
      <Note>
        PF2 Vestas: use the PSRAM build (PF2_firmware_V..._n16r8_PSRAM.bin); EBS: the
        EBS_PF2 .bin (Build/V...). The board downloads the file
        itself and only switches after checking it; if the new firmware can&apos;t reach the
        server within 2 minutes it rolls back on its own.
      </Note>
    </Card>
  );
};

const TurbineInfo = ({ windTurbine: pageTurbine, liveProps, userRole }) => {
  // Re-fetched every time the tab opens: the page's own windTurbine was
  // loaded once with the page, so after saving Owner information and
  // switching tabs, coming back showed that stale copy - looked like the
  // save had been lost (it hadn't). Also refreshes deviceInfo.
  const turbineId = pageTurbine && pageTurbine.id;
  const [{ data: fresh }, refetch] = useAxios(
    { url: `/windTurbines/${turbineId}` },
    { useCache: false, manual: !turbineId }
  );
  const onRefresh = React.useCallback(() => {
    refetch().catch(() => {});
  }, [refetch]);
  const windTurbine = fresh || pageTurbine;

  // Owner state lives here so the notifications card sees an owner e-mail
  // saved a moment ago.
  const [owner, setOwner] = useState((windTurbine && windTurbine.owner) || {});
  useEffect(() => {
    if (fresh) setOwner(fresh.owner || {});
  }, [fresh]);

  if (!windTurbine) return null;
  return (
    <Grid>
      <Information windTurbine={windTurbine} liveProps={liveProps} />
      <OwnerInformation windTurbine={windTurbine} owner={owner} setOwner={setOwner} />
      <ControllerNotifications windTurbine={windTurbine} ownerEmail={owner.email} />
      {/* PF2 Vestas boards (by schema), or any board that says it supports
          the ota_update pull command (EBS_PF2 10.6+, e.g. 009). */}
      {userRole === "ADMIN" &&
        (SERIAL_LINK_SCHEMAS.includes(windTurbine.type && windTurbine.type.schemaId) ||
          (windTurbine.deviceInfo && windTurbine.deviceInfo.otaPull)) && (
          <FirmwareUpdate windTurbine={windTurbine} onRefresh={onRefresh} />
        )}
    </Grid>
  );
};

const mapStateToProps = (state, ownProps) => ({
  liveProps: getLiveParams(state, ownProps.windTurbine && ownProps.windTurbine.id, "all"),
  userRole: state.session.user && state.session.user.role,
});

export default connect(mapStateToProps)(TurbineInfo);
